// src/components/album/LayoutRenderer.jsx
import React from 'react';
import Masonry, { ResponsiveMasonry } from 'react-responsive-masonry';
import { MediaCard } from '../../components/album//AlbumMediaCard';


export const LayoutRenderer = ({ 
  user,
  layoutConfig, 
  mediaItems, 
  galleryId, 
  gallery = null,
  onClick,
  onUpdate,
  bookId, 
  onContentCreate,
  customStyling = {}, 
  bookData = null,
  albumSettings = null,
  isOwner = false,
  onOpenModal = null,
  onRecordVoice,
  primaryColor = '#190192'
}) => {
  const { category, layout, settings = {} } = layoutConfig;

  const getBreakpoints = () => {
    // Priority 1: Gallery-level breakpoints (highest priority)
    const galleryBreakpoints = gallery?.categoryConfig?.columnsBreakpoints;
    
    // Priority 2: Album-level breakpoints (fallback)
    const albumBreakpoints = albumSettings?.columnsBreakpoints;
    
    // Priority 3: Default breakpoints (final fallback)
    const defaultBreakpoints = {
      mobile: 1,
      tablet: 2,
      desktop: 3,
      wide: 4
    };
    
    // Use the first available breakpoints configuration
    const breakpoints = galleryBreakpoints || albumBreakpoints || defaultBreakpoints;
    
    // Semantic to pixel mapping for proper responsive behavior
    const breakpointMapping = {
      mobile: 350,    // Mobile devices (< 640px)
      tablet: 768,    // Tablet devices (640px - 1024px)
      desktop: 1024,  // Desktop devices (1024px - 1280px)
      wide: 1280      // Wide screen devices (> 1280px)
    };

    const dynamicBreakpoints = {};
    
    // Convert semantic breakpoints to pixel-based breakpoints
    Object.entries(breakpointMapping).forEach(([semantic, pixels]) => {
      if (breakpoints[semantic] !== undefined) {
        dynamicBreakpoints[pixels] = parseInt(breakpoints[semantic]);
      }
    });

    // Ensure we always have at least a mobile breakpoint
    if (Object.keys(dynamicBreakpoints).length === 0) {
      return { 350: 1, 768: 2, 1024: 3, 1280: 4 };
    }

    // Sort breakpoints by pixel value to ensure proper responsive behavior
    const sortedBreakpoints = {};
    Object.keys(dynamicBreakpoints)
      .map(Number)
      .sort((a, b) => a - b)
      .forEach(pixel => {
        sortedBreakpoints[pixel] = dynamicBreakpoints[pixel];
      });

    return sortedBreakpoints;
  };

  // Render different layout types
  const renderLayout = () => {
    switch (category) {
      case 'grid':
        return renderGridLayouts();
      case 'narrative':
        return renderNarrativeLayouts();
      case 'artistic':
        return renderArtisticLayouts();
      default:
        return renderMasonryFallback();
    }
  };

  const renderGridLayouts = () => {
    switch (layout) {
      case 'masonry':
        return (
          <ResponsiveMasonry columnsCountBreakPoints={getBreakpoints()}>
            <Masonry gutter={settings.spacing === 'compact' ? '4px' : settings.spacing === 'relaxed' ? '5px' : '8px'}>
              {mediaItems.map((item, index) => (
                <div key={item.id} className="mb-0">
                  <MediaCard
                    user={user}
                    mediaItem={item}
                    galleryId={galleryId}
                    gallery={gallery}
                    bookId={bookId}
                    index={index}
                    onClick={onClick}
                    onUpdate={onUpdate}
                    onContentCreate={onContentCreate}
                    customStyling={customStyling}
                    bookData={bookData}
                    onRecordVoice={onRecordVoice}
                    albumSettings={albumSettings}
                    isOwner={isOwner}
                    onOpenModal={onOpenModal}
                    primaryColor={primaryColor}
                  />
                </div>
              ))}
            </Masonry>
          </ResponsiveMasonry>
        );

      case 'uniformGrid':
        return (
          <div 
            className="grid gap-4"
            style={{
              gridTemplateColumns: `repeat(auto-fit, minmax(${settings.minCardWidth || '280px'}, 1fr))`,
              gap: settings.spacing === 'compact' ? '8px' : settings.spacing === 'relaxed' ? '24px' : '16px'
            }}
          >
            {mediaItems.map((item, index) => (
              <div key={item.id} className="aspect-square">
                <MediaCard
                  user={user}
                  mediaItem={item}
                  galleryId={galleryId}
                  gallery={gallery}
                  bookId={bookId}
                  index={index}
                  onClick={onClick}
                  onUpdate={onUpdate}
                  onContentCreate={onContentCreate}
                  customStyling={customStyling}
                  bookData={bookData}
                  onRecordVoice={onRecordVoice}
                  albumSettings={albumSettings}
                  isOwner={isOwner}
                  onOpenModal={onOpenModal}
                  primaryColor={primaryColor}
                />
              </div>
            ))}
          </div>
        );

      case 'bento':
        return (
          <div className="bento-grid" style={{ gap: settings.spacing === 'compact' ? '8px' : '16px' }}>
            {mediaItems.map((item, index) => {
              // Create varied sizing for bento layout
              const getBentoSize = (index) => {
                const patterns = [
                  'col-span-2 row-span-2', // Large
                  'col-span-1 row-span-1', // Small
                  'col-span-1 row-span-2', // Tall
                  'col-span-2 row-span-1', // Wide
                ];
                return patterns[index % patterns.length];
              };

              return (
                <div key={item.id} className={`${getBentoSize(index)}`}>
                  <MediaCard
                    user={user}
                    mediaItem={item}
                    galleryId={galleryId}
                    gallery={gallery}
                    bookId={bookId}
                    onClick={onClick}
                    onUpdate={onUpdate}
                    onContentCreate={onContentCreate}
                    index={index}
                    customStyling={customStyling}
                    bookData={bookData}
                    onRecordVoice={onRecordVoice}
                    albumSettings={albumSettings}
                    isOwner={isOwner}
                    onOpenModal={onOpenModal}
                    primaryColor={primaryColor}
                  />
                </div>
              );
            })}
          </div>
        );

      default:
        return renderMasonryFallback();
    }
  };

  const renderNarrativeLayouts = () => {
    switch (layout) {
      case 'heroFlow':
        const [heroItem, ...restItems] = mediaItems;
        return (
          <div className="space-y-6">
            {/* Hero Section */}
            {heroItem && (
              <div className="hero-section mb-8">
                <MediaCard
                  user={user}
                  mediaItem={heroItem}
                  galleryId={galleryId}
                  gallery={gallery}
                  bookId={bookId}
                  index={0} 
                  onClick={onClick}
                  onUpdate={onUpdate}
                  onContentCreate={onContentCreate}
                  customStyling={customStyling}
                  bookData={bookData}
                  onRecordVoice={onRecordVoice}
                  albumSettings={albumSettings}
                  isOwner={isOwner}
                  onOpenModal={onOpenModal}
                  primaryColor={primaryColor}
                />
              </div>
            )}
            
            {/* Supporting Media */}
            {restItems.length > 0 && (
              <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                {restItems.map((item, index) => (
                  <MediaCard
                    user={user}
                    mediaItem={item}
                    galleryId={galleryId}
                    gallery={gallery}
                    bookId={bookId}
                    onClick={onClick}
                    onUpdate={onUpdate}
                    onContentCreate={onContentCreate}
                    index={index}
                    customStyling={customStyling}
                    bookData={bookData}
                    onRecordVoice={onRecordVoice}
                    albumSettings={albumSettings}
                    isOwner={isOwner}
                    onOpenModal={onOpenModal}
                    primaryColor={primaryColor}
                  />
                ))}
              </div>
            )}
          </div>
        );

      case 'timelineFlow':
        return (
          <div className="timeline-layout space-y-8">
            {mediaItems.map((item, index) => (
              <div key={item.id} className={`flex ${index % 2 === 0 ? 'flex-row' : 'flex-row-reverse'} gap-6 items-center`}>
                <div className="w-1/2">
                  <MediaCard
                    user={user}
                    mediaItem={item}
                    galleryId={galleryId}
                    gallery={gallery}
                    bookId={bookId}
                    index={index}  
                    onClick={onClick}
                    onUpdate={onUpdate}
                    onContentCreate={onContentCreate}
                    customStyling={customStyling}
                    bookData={bookData}
                    onRecordVoice={onRecordVoice}
                    albumSettings={albumSettings}
                    isOwner={isOwner}
                    onOpenModal={onOpenModal}
                    primaryColor={primaryColor}
                  />
                </div>
                <div className="w-1/2 px-4">
                  <div className="text-lg font-semibold text-gray-900 mb-2">
                    {item.title || `Memory ${index + 1}`}
                  </div>
                  <div className="text-gray-600">
                    {item.description || 'A special moment captured in time.'}
                  </div>
                </div>
              </div>
            ))}
          </div>
        );

      default:
        return renderMasonryFallback();
    }
  };

  const renderArtisticLayouts = () => {
    switch (layout) {
      case 'constellation':
        return (
          <div className="constellation-layout relative min-h-screen p-8">
            {mediaItems.map((item, index) => {
              // Create organic positioning
              const getConstellationPosition = (index) => {
                const positions = [
                  { top: '10%', left: '15%', size: 'w-32 h-32' },
                  { top: '30%', left: '60%', size: 'w-40 h-40' },
                  { top: '60%', left: '20%', size: 'w-28 h-28' },
                  { top: '20%', left: '80%', size: 'w-36 h-36' },
                  { top: '80%', left: '70%', size: 'w-32 h-32' },
                  { top: '70%', left: '50%', size: 'w-44 h-44' },
                ];
                return positions[index % positions.length];
              };

              const position = getConstellationPosition(index);
              
              return (
                <div
                  key={item.id}
                  className={`absolute ${position.size} rounded-full overflow-hidden hover:scale-110 transition-transform cursor-pointer`}
                  style={{ top: position.top, left: position.left }}
                >
                  <MediaCard
                    user={user}
                    mediaItem={item}
                    galleryId={galleryId}
                    gallery={gallery}
                    bookId={bookId}
                    index={index} 
                    onClick={onClick}
                    onUpdate={onUpdate}
                    onContentCreate={onContentCreate}
                    customStyling={customStyling}
                    bookData={bookData}
                    onRecordVoice={onRecordVoice}
                    albumSettings={albumSettings}
                    isOwner={isOwner}
                    onOpenModal={onOpenModal}
                    primaryColor={primaryColor}
                  />
                </div>
              );
            })}
          </div>
        );

      default:
        return renderMasonryFallback();
    }
  };

  const renderMasonryFallback = () => {
    return (
      <ResponsiveMasonry columnsCountBreakPoints={getBreakpoints()}>
        <Masonry gutter="16px">
          {mediaItems.map((item, index) => (  // ✅ Add index
          <div key={item.id} className="mb-4">
            <MediaCard
              user={user}
              mediaItem={item}
              index={index} 
              galleryId={galleryId}
              gallery={gallery}
              bookId={bookId}
              onClick={onClick}
              customStyling={customStyling}
              bookData={bookData}
              onRecordVoice={onRecordVoice}
              albumSettings={albumSettings}
              isOwner={isOwner}
              onOpenModal={onOpenModal}
              primaryColor={primaryColor}
            />
            </div>
          ))}
        </Masonry>
      </ResponsiveMasonry>
    );
  };

  return (
    <div className="layout-renderer">
      {renderLayout()}
      
      {/* Add CSS for bento grid */}
      <style jsx>{`
        .bento-grid {
          display: grid;
          grid-template-columns: repeat(4, 1fr);
          grid-auto-rows: minmax(150px, auto);
          grid-auto-flow: dense;
        }
        
        @media (max-width: 768px) {
          .bento-grid {
            grid-template-columns: repeat(2, 1fr);
          }
        }
      `}</style>
    </div>
  );
};